import React, { useState, useEffect, Suspense, lazy, useContext, createContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { apiClient } from './services/api/client';
import { UserRoleProvider, RoleGuard } from './contexts/UserRoleContext';
import { TenantProvider } from './contexts/TenantContext';
import { FindingDrawerProvider } from './contexts/FindingDrawerContext';
import { AIAdvisorProvider } from './contexts/AIAdvisorContext';
import Sidebar from './components/Layout/Sidebar';
import TopNav from './components/Layout/TopNav';
import ErrorBoundary from './components/Layout/ErrorBoundary';
import { ToastProvider, useToast } from './components/Layout/ToastContainer';
import { DialogProvider } from './components/Common/DialogProvider';
import NotFoundPage from './components/Common/NotFoundPage';
import AccessDeniedPage from './components/Common/AccessDeniedPage';
import OnboardingWizard from './components/Onboarding/OnboardingWizard';
import LoginPage from './components/Auth/LoginPage';
import AIAssistantWidget from './components/AIAssistant/AIAssistantWidget';
// BoardView and AllBoardsPage are lazy-loaded below with other components
import Breadcrumb from './components/Common/Breadcrumb';
import TopProgressBar from './components/Common/TopProgressBar';
import { PageSkeleton } from './components/Common/Skeleton';
import AlertBanner from './components/Common/AlertBanner';
import ApiFailureBanner from './components/Common/ApiFailureBanner';
import CommandPalette from './components/Common/CommandPalette';
import KeyboardShortcutsModal from './components/Common/KeyboardShortcutsModal';
import { useSecurityEvents } from './hooks/useSecurityEvents';
import { useGlobalShortcuts } from './hooks/useGlobalShortcuts';
import { getBoardIcon } from './shared/boardIcons';
import './index.css';
import './dark-mode.css';

// ── Dark Mode Context ────────────────────────────────────────────────────────
const ThemeContext = createContext({ darkMode: false, toggleDarkMode: () => {} });
export const useTheme = () => useContext(ThemeContext);

// ── Lazy-loaded route components ─────────────────────────────────────────────
// Wiz-style navigation: ~10 main sidebar pages.
// CSPM/CWPP/CIEM/DSPM/KSPM are NOT separate pages — they are domains
// that feed into the unified Issues page and Inventory as filters/tabs.
// Sub-features (remediation, workflows, RBAC, audit, etc.) live inside
// their parent page as tabs, not as top-level routes.

// ── MAIN SIDEBAR PAGES (matches Wiz structure) ──
const OverviewDashboard           = lazy(() => import('./components/Overview/OverviewDashboard'));
const IssuesHub                   = lazy(() => import('./components/Issues/IssuesHub'));
// Round 22 (2026-05-16): public-share read-only viewer. Bypasses sidebar
// chrome + auth check via the early-return in App's render. Backend pairing
// at GET /api/v1/issues/public/{id}?token=X&exp=Y&t=tenant_id.
const PublicSharedIssue           = lazy(() => import('./components/Issues/PublicSharedIssue'));
const SuppressionRulesPage        = lazy(() => import('./components/SuppressionRules/SuppressionRulesPage'));
const PosturePoliciesPage         = lazy(() => import('./components/PosturePolicies/PosturePoliciesPage'));
const CrownJewelsPage             = lazy(() => import('./components/CrownJewels/CrownJewelsPage'));
const CustomDashboardsPage        = lazy(() => import('./components/Dashboards/CustomDashboardsPage'));
const GraphHub                    = lazy(() => import('./components/Graph/GraphHub'));
const WorkflowsDashboard          = lazy(() => import('./components/Workflows/WorkflowsDashboard'));
const InventoryDashboard          = lazy(() => import('./components/Inventory/InventoryDashboard'));
const ThreatCenterDashboard       = lazy(() => import('./components/ThreatCenter/ThreatCenterDashboard'));
const DefendDashboard             = lazy(() => import('./components/Defend/DefendDashboard'));
const PoliciesDashboard           = lazy(() => import('./components/Policies/PoliciesDashboard'));
const ComplianceDashboard         = lazy(() => import('./components/Compliance/EnhancedComplianceDashboard'));
const RemediationDashboard        = lazy(() => import('./components/Remediation/RemediationDashboard'));
const AnalyticsDashboard          = lazy(() => import('./components/Analytics/AnalyticsDashboard'));
const ReportsDashboard            = lazy(() => import('./components/Reports/ReportsDashboard'));
const SettingsPage                = lazy(() => import('./components/Settings/SettingsPage'));
const AllBoardsPage               = lazy(() => import('./components/Boards/AllBoardsPage'));
const BoardView                   = lazy(() => import('./components/Boards/BoardView'));

// ── SECONDARY PAGES (accessible via sidebar sub-items or deep links) ──
const RuntimeDashboard            = lazy(() => import('./components/RuntimeSensor/RuntimeDashboard'));
const InvestigationsDashboard     = lazy(() => import('./components/Investigations/InvestigationsDashboard'));
const IntegrationsCatalog         = lazy(() => import('./components/IntegrationsHub/IntegrationsCatalog'));
const TenantManagerDashboard      = lazy(() => import('./components/TenantManager/TenantManagerDashboard'));
const ProjectsDashboard           = lazy(() => import('./components/Projects/ProjectsDashboard'));
// R181-C (2026-05-19) — Stage-13 R180 cleanup.
// Removed legacy `CustomDashboard` (localStorage-backed) and its
// `/custom-dashboard` route. R140 introduced `Dashboards/CustomDashboardsPage`
// (DB-backed, multi-user) routed at `/dashboards` + `/dashboards/:id` —
// canonical. The legacy file under components/Dashboard/ has been deleted
// and the OverviewDashboard tile that linked to /custom-dashboard now
// redirects to /dashboards.
const ExposureManagementDashboard = lazy(() => import('./components/ExposureManagement/ExposureManagementDashboard'));
const DetectionRulesLibrary       = lazy(() => import('./components/DetectionRules/DetectionRulesLibrary'));
const APIKeysPage                  = lazy(() => import('./components/Admin/APIKeysPage'));
const WebhooksPage                 = lazy(() => import('./components/Admin/WebhooksPage'));
// R193-SHIP-O 2026-05-19 — per-tenant blast-radius operator view.
const AdminTenantBlastRadius       = lazy(() => import('./components/Admin/AdminTenantBlastRadius'));

// ── DOMAIN-SPECIFIC PAGES (deep-link accessible, but not top-level sidebar) ──
// These exist as dedicated components for deep-linking and drill-down,
// but in Wiz-style UI they're accessed from within Issues/Inventory/Graph.
const DSPMDashboard               = lazy(() => import('./components/DSPM/DSPMDashboard'));
const CSPMDashboard               = lazy(() => import('./components/CSPM/CSPMDashboard'));
const CWPPDashboard               = lazy(() => import('./components/CWPP/CWPPDashboard'));
const CIEMDashboard               = lazy(() => import('./components/CIEM/CIEMDashboard'));
const CDRDashboard                = lazy(() => import('./components/CDR/EnhancedCDRDashboard'));
const ContainerK8sDashboard       = lazy(() => import('./components/ContainerK8s/ContainerK8sDashboard'));
const AISPMDashboard              = lazy(() => import('./components/AISPM/AISPMDashboard'));
const VulnerabilityDashboard      = lazy(() => import('./components/VulnerabilityManagement/VulnerabilityDashboard'));
const KSPMDashboard               = lazy(() => import('./components/KSPM/KSPMDashboard'));
const SupplyChainDashboard        = lazy(() => import('./components/SupplyChain/SupplyChainDashboard'));
const CodeSecurityDashboard       = lazy(() => import('./components/CodeSecurity/CodeSecurityDashboard'));
const EASMDashboard               = lazy(() => import('./components/EASM/EASMDashboard'));
// FindingsPage restored — Wiz keeps Findings (raw scanner output) and Issues
// (deduplicated wrappers) as separate pages. Conflating them lost the Findings
// view entirely.
const FindingsPage                = lazy(() => import('./components/Findings/FindingsPage'));
const CodeToCloudPage             = lazy(() => import('./components/CodeToCloud/CodeToCloudPage'));
const EnhancedAttackPathsDashboard = lazy(() => import('./components/AttackPaths/EnhancedAttackPathsDashboard'));
const RiskQueue                    = lazy(() => import('./components/RiskQueue/RiskQueue'));
const NLQueryPanel                 = lazy(() => import('./components/Graph/NLQueryPanel'));
const ThreatsPage                  = lazy(() => import('./components/Threats/ThreatsPage'));
// Recently un-orphaned components (wired in this session)
// `/ask-ai` used to render a second, redundant free-text chat panel
// (`AskAIPanel`, backed by /api/v1/ai/query) that no nav ever linked to.
// Item #4 converges on ONE advisor: the global grounded `AIAssistantWidget`.
// Any lingering `/ask-ai` link now just opens that widget and lands home.
const AskAIRedirect: React.FC = () => {
  useEffect(() => { window.dispatchEvent(new CustomEvent('open-ai-assistant')); }, []);
  return <Navigate to="/" replace />;
};
const AttackerSimulationPage       = lazy(() => import('./components/AttackerSimulation/AttackerSimulationPage'));
const EventScannerDashboard        = lazy(() => import('./components/EventScanner/EventScannerDashboard'));
const ABACPolicyBuilder            = lazy(() => import('./components/CIEM/ABACPolicyBuilder'));
const ComplianceHeatmap            = lazy(() => import('./components/Compliance/ComplianceHeatmap'));

// ── Entity-typed detail routes (Wiz-style deep links) ──
// Wrappers fetch the entity by :id and render the existing detail component
// inline. The list page underneath stays mounted via location-state, so going
// back restores list scroll + filter state without a refetch.
const IssueDetail                  = lazy(() => import('./components/Issues/IssueDetail'));
const FindingDetail                = lazy(() => import('./components/Findings/FindingDetail'));
const ResourceDetail               = lazy(() => import('./components/Inventory/ResourceDetail'));
const IdentityDetail               = lazy(() => import('./components/CIEM/IdentityDetail'));

// Real-time critical event notifier
const SecurityEventNotifier: React.FC = () => {
  const { latest } = useSecurityEvents();
  const { addToast } = useToast();

  useEffect(() => {
    if (!latest) return;
    if (latest.type === 'toxic_combination.detected') {
      addToast(`⚠ Toxic combo: ${latest.title}`, 'error', 8000);
    } else if (latest.severity === 'critical' && latest.type === 'finding.created') {
      addToast(`Critical finding: ${latest.title}`, 'error', 6000);
    } else if (latest.type === 'scan.completed') {
      addToast(latest.title, 'success', 4000);
    } else if (latest.type === 'issue.resolved') {
      // R193-SHIP-AN: operator actions surface as info toasts.
      addToast(latest.title, 'success', 4000);
    } else if (latest.type === 'issue.suppressed' || latest.type === 'issue.snoozed') {
      addToast(latest.title, 'info', 4000);
    } else if (latest.type === 'issue.assigned') {
      addToast(latest.title, 'info', 4000);
    } else if (latest.type === 'issue.severity_changed') {
      // R193-SHIP-AN: only toast when NEW severity is critical/high (P0/P1
      // equivalents). Per-task spec to avoid notification floods on medium/low
      // re-classifications.
      const rawAny = latest.raw as Record<string, unknown>;
      const newSev = String(
        (rawAny.new_severity as string | undefined)
        || ((rawAny.issue as Record<string, unknown> | undefined)?.severity as string | undefined)
        || ''
      ).toLowerCase();
      if (newSev === 'critical' || newSev === 'high') {
        addToast(latest.title, 'warning', 5000);
      }
    } else if (latest.type === 'issue.priority_changed') {
      // R193-SHIP-AN: only toast when NEW priority is P0 or P1.
      const rawAny = latest.raw as Record<string, unknown>;
      const newPri = String(
        (rawAny.new_priority as string | undefined)
        || ((rawAny.issue as Record<string, unknown> | undefined)?.priority as string | undefined)
        || ''
      ).toUpperCase();
      if (newPri === 'P0' || newPri === 'P1') {
        addToast(latest.title, 'warning', 5000);
      }
    }
  }, [latest, addToast]);

  return null;
};

// Shared loading fallback. Wiz shows content-shaped skeletons on every route
// transition, not a bare spinner — perceived performance is much higher because
// the page layout appears instantly and only the data shimmers in.
const PageLoader: React.FC = () => (
  <div role="status" aria-label="Loading page">
    <PageSkeleton />
    <span className="sr-only">Loading page content...</span>
  </div>
);

// Wrapper that provides navigate as onNavigate prop for backward compat
const WithNavigate: React.FC<{ Component: React.ComponentType<{ onNavigate: (p: string) => void }> }> = ({ Component }) => {
  const navigate = useNavigate();
  return <Component onNavigate={(p: string) => navigate(p)} />;
};

/** R144 — Custom Boards section in the sidebar flyout. Fetches from
 *  /api/v1/dashboards/custom and lists each board with click-to-open + a
 *  "+ New Board" link to the full editor at /dashboards. Loaded lazily
 *  the first time the flyout opens. */
const CustomBoardsFlyoutSection: React.FC<{ navigate: (p: string) => void; currentPath: string }> = ({ navigate, currentPath }) => {
  const [boards, setBoards] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { customDashboardsApi } = await import('./services/api');
        const v: any = await customDashboardsApi.list();
        if (!cancelled) {
          setBoards(Array.isArray(v) ? v : v?.dashboards || []);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1 mt-3 flex items-center justify-between">
        <span>Custom Boards</span>
        <button
          onClick={() => navigate('/dashboards')}
          className="text-purple-400 hover:text-purple-300 normal-case tracking-normal text-xs font-medium"
          title="Open Custom Dashboards manager"
        >
          + New
        </button>
      </div>
      {!loaded ? (
        <div className="px-2 py-1 text-[11px] text-gray-600">Loading...</div>
      ) : boards.length === 0 ? (
        <button
          onClick={() => navigate('/dashboards')}
          className="w-full text-left px-2 py-1.5 rounded text-[11px] text-gray-600 hover:text-gray-400 hover:bg-gray-800/50 italic"
        >
          No custom boards yet — click to create
        </button>
      ) : (
        boards.map((b: any) => (
          <button
            key={b.id}
            onClick={() => navigate(`/dashboards/${b.id}`)}
            className={`w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 ${
              currentPath === `/dashboards/${b.id}` ? 'text-gray-200 bg-gray-800' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <span className="text-sm">{b.is_default ? '⭐' : b.is_shared ? '👥' : '📋'}</span>
            <span className="truncate flex-1">{b.name}</span>
            <span className="text-xs text-gray-600">{(b.widgets || []).length}</span>
          </button>
        ))
      )}
    </>
  );
};

/** Route component for /boards — renders the canonical AllBoardsPage
 *  (categorized Default / Managed boards). Previously this was a flat inline
 *  duplicate of AllBoardsPage's board list, which diverged from the real
 *  component and dropped the category grouping. Converged to the single source. */
const AllBoardsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading boards…</div>}>
      <AllBoardsPage onNavigate={(p: string) => navigate(p)} />
    </Suspense>
  );
};

/** Route component for /boards/:boardId */
const BoardViewRoute: React.FC = () => {
  const navigate = useNavigate();
  // No board id in the path → pass empty; BoardView renders an honest empty
  // state (the old 'board-overview' default 500'd on the BE ::uuid cast).
  const params = window.location.pathname.split('/boards/')[1] || '';
  return <BoardView boardId={params} onNavigate={(p: string) => navigate(p)} />;
};


// ── Entity-typed route wrappers (/issue|resource|identity|finding/:id) ──
// Each wrapper:
//   1. reads :id from the URL via useParams,
//   2. fetches the entity via the corresponding API,
//   3. renders the existing detail component as a full-page slide-over
//      (the components own their own chrome — so deep-links work without
//      a list page mounted underneath).
// `onClose` navigates -1 to restore the prior route (list view) when the
// user came from a list page, or sends them to the appropriate list page
// when the URL was opened directly.
const IssueDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [issue, setIssue] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const { findingsApi } = await import('./services/api');
        const data = await findingsApi.getIssue(id);
        if (!cancelled) setIssue(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load issue');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);
  const onClose = () => {
    // If we have history state from a list view, pop back; else go to /issues
    if (location.key !== 'default') navigate(-1);
    else navigate('/issues');
  };
  if (error) {
    return (
      <div style={{ padding: 24, color: '#f87171' }}>
        Failed to load issue {id}: {error}
        <button onClick={onClose} style={{ marginLeft: 12, color: '#a5b4fc' }}>Back</button>
      </div>
    );
  }
  if (!issue) return <PageLoader />;
  return <IssueDetail issue={issue} onClose={onClose} />;
};

const ResourceDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [resource, setResource] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const { inventoryApi } = await import('./services/api');
        const data = await inventoryApi.getResource(id);
        if (!cancelled) setResource(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load resource');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);
  const onClose = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/inventory');
  };
  if (error) {
    return (
      <div style={{ padding: 24, color: '#f87171' }}>
        Failed to load resource {id}: {error}
        <button onClick={onClose} style={{ marginLeft: 12, color: '#a5b4fc' }}>Back</button>
      </div>
    );
  }
  if (!resource) return <PageLoader />;
  return <ResourceDetail resource={resource} onClose={onClose} />;
};

const IdentityDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const onBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/ciem');
  };
  if (!id) return <PageLoader />;
  // IdentityDetail accepts identityId + onBack and fetches the identity
  // itself via ciemApi.getIdentity in its own useEffect.
  return <IdentityDetail identityId={id} onBack={onBack} />;
};

// /finding/:id is an alias for /issue/:id (Wiz unifies findings into issues).
// /finding/:id — deep-link/shared route for a single finding. A finding id is
// NOT an issue id (findings carry fingerprint ids; issues aggregate findings
// and carry UUIDs), so the old `<Navigate to={/issue/:id}>` always 404'd. Fetch
// the finding directly and render the same drawer the Findings list uses.
const FindingDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [finding, setFinding] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const { findingsApi } = await import('./services/api');
        const data = await findingsApi.getFinding(id);
        if (!cancelled) setFinding(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load finding');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);
  const onClose = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/findings');
  };
  if (error) {
    return (
      <div style={{ padding: 24, color: '#f87171' }}>
        Failed to load finding {id}: {error}
        <button onClick={onClose} style={{ marginLeft: 12, color: '#a5b4fc' }}>Back</button>
      </div>
    );
  }
  if (!finding) return <PageLoader />;
  return <FindingDetail finding={finding} onClose={onClose} />;
};

// Inner app component that uses auth context + React Router
const AppRoutes: React.FC = () => {
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [boardsFlyoutOpen, setBoardsFlyoutOpen] = useState(false);
  const [findingsFlyoutOpen, setFindingsFlyoutOpen] = useState(false);
  const [findingsCategory, setFindingsCategory] = useState('all');
  const [issuesFlyoutOpen, setIssuesFlyoutOpen] = useState(false);
  const [threatsFlyoutOpen, setThreatsFlyoutOpen] = useState(false);
  const { user, loading, isAuthenticated, getAuthToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Global `?` shortcut sheet (Wiz parity). Lifted out of IssuesPage so the
  // help affordance works on every route.
  const shortcuts = useGlobalShortcuts();

  useEffect(() => {
    const inIframe = window.self !== window.top;
    setIsEmbedded(inIframe);
  }, []);

  // Ctrl+K / Cmd+K to toggle command palette. `/` and the TopNav search box
  // open the same one: there used to be a SECOND global search living in
  // TopNav that queried the identical /api/v1/data-plane/search endpoint and
  // rendered its own dropdown, so the same keystrokes produced two different
  // result lists depending on which affordance you reached for.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };
    const openHandler = () => setCommandPaletteOpen(true);
    document.addEventListener('keydown', handler);
    window.addEventListener('open-command-palette', openHandler);
    return () => {
      document.removeEventListener('keydown', handler);
      window.removeEventListener('open-command-palette', openHandler);
    };
  }, []);

  // Expose auth context to browser console for debugging
  useEffect(() => {
    const token = getAuthToken();
    (window as any).__authContext = {
      user,
      isAuthenticated,
      token,
      tenant: localStorage.getItem('tenant_id') || 'default',
      inspect: () => {
        console.group('%c[Auth] Current Context', 'color:#818cf8;font-weight:bold');
        console.log('Authenticated:', isAuthenticated);
        console.log('User:', user);
        console.log('Token:', token);
        console.log('Tenant:', localStorage.getItem('tenant_id') || 'default');
        console.groupEnd();
      },
    };
    if (!loading) {
      console.log(
        `%c[Auth] ${isAuthenticated ? '✓ Authenticated as ' + user?.email : '✗ Not authenticated'}`,
        `color:${isAuthenticated ? '#4ade80' : '#f87171'};font-weight:bold`
      );
      console.log('[Auth] Run window.__authContext.inspect() to see full auth context');
    }
  }, [user, isAuthenticated, loading, getAuthToken]);

  // Show onboarding only on a genuinely fresh environment. If a cloud account is
  // already connected (an existing deployment, or a new browser hitting a live
  // tenant), skip the wizard instead of hijacking every route — previously any
  // authenticated browser without a localStorage flag was force-redirected to
  // /onboarding, so the whole populated app was hidden behind the setup wizard.
  useEffect(() => {
    if (loading || !isAuthenticated) return;
    const done = localStorage.getItem('onboarding_complete') === 'true' || localStorage.getItem('onboarding_completed') === 'true';
    if (done) { setOnboardingChecked(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get<any>('/api/v1/connectors');
        const n = (typeof data?.total === 'number' ? data.total : 0)
          || (Array.isArray(data?.connectors) ? data.connectors.length : 0);
        if (cancelled) return;
        if (n > 0) {
          // Environment already set up — mark complete so no route is hijacked.
          localStorage.setItem('onboarding_completed', 'true');
        } else {
          setShowOnboarding(true);
        }
      } catch {
        // On error, don't trap the user in onboarding.
        if (!cancelled) localStorage.setItem('onboarding_completed', 'true');
      } finally {
        if (!cancelled) setOnboardingChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, isAuthenticated, getAuthToken]);

  // ── Wiz-style routing ─────────────────────────────────────────────────
  // MAIN SIDEBAR (~10 items): Overview, Issues, Graph, Inventory,
  //   Threats, Policies, Compliance, Actions, Reports, Settings
  // Domain dashboards (CSPM/CWPP/CIEM/etc.) are deep-link accessible
  //   but NOT top-level sidebar items — they are filters/tabs within
  //   the main pages (Issues, Inventory, Graph).
  const routes = (
    <Routes>
      {/* ── ONBOARDING ──────────────────────────────────────────────── */}
      <Route path="/onboarding" element={<OnboardingWizard onComplete={() => { setShowOnboarding(false); window.location.href = '/overview'; }} />} />

      {/* ── MAIN SIDEBAR PAGES ─────────────────────────────────────── */}
      <Route path="/" element={<Navigate to="/overview" replace />} />
      <Route path="/overview" element={
        onboardingChecked && localStorage.getItem('onboarding_completed') !== 'true' && localStorage.getItem('onboarding_complete') !== 'true'
          ? <Navigate to="/onboarding" replace />
          : <WithNavigate Component={OverviewDashboard} />
      } />
      <Route path="/boards" element={<AllBoardsRoute />} />
      <Route path="/boards/:boardId" element={<BoardViewRoute />} />
      <Route path="/issues" element={<WithNavigate Component={IssuesHub} />} />
      <Route path="/suppression-rules" element={<WithNavigate Component={SuppressionRulesPage} />} />
      <Route path="/posture-policies" element={<WithNavigate Component={PosturePoliciesPage} />} />
      <Route path="/crown-jewels" element={<WithNavigate Component={CrownJewelsPage} />} />
      <Route path="/dashboards" element={<WithNavigate Component={CustomDashboardsPage} />} />
      <Route path="/dashboards/:dashboardId" element={<WithNavigate Component={CustomDashboardsPage} />} />
      <Route path="/threats" element={<WithNavigate Component={ThreatsPage} />} />
      <Route path="/graph" element={<GraphHub />} />
      <Route path="/inventory" element={<InventoryDashboard />} />
      <Route path="/defend" element={<DefendDashboard />} />
      {/* R171-E (2026-05-19): G-TI-22 — un-orphan ThreatCenterDashboard.
          Pre-R171-E: route redirected to /defend, leaving the dedicated page unreachable. */}
      <Route path="/threat-center" element={<ThreatCenterDashboard />} />
      <Route path="/policies" element={<PoliciesDashboard />} />
      <Route path="/compliance" element={<ComplianceDashboard />} />
      <Route path="/remediation" element={<RemediationDashboard />} />
      <Route path="/analytics" element={<AnalyticsDashboard />} />
      <Route path="/reports" element={<ReportsDashboard />} />
      <Route path="/settings" element={<RoleGuard requiredRole="admin" fallback={<AccessDeniedPage page="Settings" />}><SettingsPage /></RoleGuard>} />

      {/* ── SECONDARY SIDEBAR (sub-items) ──────────────────────────── */}
      <Route path="/runtime" element={<Navigate to="/defend" replace />} />
      <Route path="/investigations" element={<InvestigationsDashboard />} />
      <Route path="/integrations-hub" element={<IntegrationsCatalog />} />
      <Route path="/exposure" element={<ExposureManagementDashboard />} />
      <Route path="/projects" element={<WithNavigate Component={ProjectsDashboard} />} />
      {/* R181-C (2026-05-19) — Stage-13 R180: legacy /custom-dashboard removed.
          Redirect any external bookmarks to the DB-backed page at /dashboards. */}
      <Route path="/custom-dashboard" element={<Navigate to="/dashboards" replace />} />
      <Route path="/tenant-manager" element={<RoleGuard requiredRole="admin" fallback={<AccessDeniedPage page="Tenant Manager" />}><TenantManagerDashboard /></RoleGuard>} />
      <Route path="/detection-rules" element={<DetectionRulesLibrary />} />
      <Route path="/admin/api-keys" element={<RoleGuard requiredRole="admin" fallback={<AccessDeniedPage page="API Keys" />}><APIKeysPage /></RoleGuard>} />
      <Route path="/admin/webhooks" element={<RoleGuard requiredRole="admin" fallback={<AccessDeniedPage page="Webhooks" />}><WebhooksPage /></RoleGuard>} />
      <Route path="/admin/tenants/blast-radius" element={<RoleGuard requiredRole="admin" fallback={<AccessDeniedPage page="Tenant Blast Radius" />}><AdminTenantBlastRadius /></RoleGuard>} />

      {/* ── DOMAIN DEEP-LINKS (accessible via Issues/Inventory tabs) ─ */}
      <Route path="/cspm" element={<CSPMDashboard />} />
      <Route path="/cwpp" element={<CWPPDashboard />} />
      <Route path="/ciem" element={<CIEMDashboard />} />
      <Route path="/dspm" element={<DSPMDashboard />} />
      <Route path="/cdr" element={<Navigate to="/defend" replace />} />
      <Route path="/container-k8s" element={<ContainerK8sDashboard />} />
      <Route path="/ai-spm" element={<AISPMDashboard />} />
      <Route path="/vulnerabilities" element={<VulnerabilityDashboard />} />
      <Route path="/kspm" element={<KSPMDashboard />} />
      <Route path="/supply-chain" element={<SupplyChainDashboard />} />
      <Route path="/code-security" element={<CodeSecurityDashboard />} />
      <Route path="/easm" element={<EASMDashboard />} />
      {/* Findings: raw scanner output (csp_core.findings, ~30K rows after a full scan).
          Distinct from /issues (deduplicated wrappers). Wiz keeps these separate. */}
      <Route path="/findings" element={<FindingsPage />} />

      {/* ── Entity-typed detail routes (Wiz-style deep links) ─────── */}
      <Route path="/issue/:id" element={<IssueDetailRoute />} />
      <Route path="/resource/:id" element={<ResourceDetailRoute />} />
      <Route path="/identity/:id" element={<IdentityDetailRoute />} />
      <Route path="/finding/:id" element={<FindingDetailRoute />} />

      <Route path="/attack-paths" element={<EnhancedAttackPathsDashboard />} />
      <Route path="/risk-queue" element={<RiskQueue />} />
      <Route path="/query" element={<NLQueryPanel />} />
      <Route path="/code-to-cloud" element={<CodeToCloudPage />} />

      {/* ── Wiz-style advanced tools (wired from orphaned components) ──── */}
      <Route path="/ask-ai" element={<AskAIRedirect />} />
      <Route path="/attack-simulation" element={<AttackerSimulationPage />} />
      <Route path="/event-scanner" element={<EventScannerDashboard />} />
      <Route path="/policies/abac-builder" element={<ABACPolicyBuilder />} />
      <Route path="/compliance/heatmap" element={<ComplianceHeatmap />} />

      {/* ── SETTINGS SUB-PAGES (tabs inside Settings) ──────────────── */}
      {/* Each of these MUST carry ?tab=<id>: SettingsPage falls back to the
          'connectors' tab whenever ?tab is missing, so a bare /settings
          redirect silently served the Connectors page. /audit-logs,
          /notifications and /rbac all rendered "Manage cloud accounts,
          clusters, and integrations" — a different page than the one asked
          for, with no error to reveal it. Tab ids come from SETTINGS_TABS. */}
      <Route path="/accounts" element={<Navigate to="/settings?tab=connectors" replace />} />
      <Route path="/connectors" element={<Navigate to="/settings?tab=connectors" replace />} />
      <Route path="/integrations" element={<Navigate to="/integrations-hub" replace />} />
      <Route path="/notifications" element={<Navigate to="/settings?tab=notifications" replace />} />
      <Route path="/rbac" element={<Navigate to="/settings?tab=users-access" replace />} />
      <Route path="/workflows" element={<WorkflowsDashboard />} />
      <Route path="/audit-logs" element={<Navigate to="/settings?tab=audit" replace />} />

      {/* ── LEGACY REDIRECTS ───────────────────────────────────────── */}
      <Route path="/runtime-protection" element={<Navigate to="/defend" replace />} />
      <Route path="/engines" element={<Navigate to="/policies" replace />} />
      {/* /simulate predates the attack-simulation page: when this alias was
          written there was no simulation surface, so it dumped the user on
          Overview. AttackerSimulationPage is now wired at /attack-simulation
          (see the "advanced tools" block above), so point the alias at the
          page it names instead of silently serving a different one. */}
      <Route path="/simulate" element={<Navigate to="/attack-simulation" replace />} />
      <Route path="/cost-security" element={<Navigate to="/analytics" replace />} />
      <Route path="/cis-benchmarks" element={<Navigate to="/compliance" replace />} />
      <Route path="/custom-detectors" element={<Navigate to="/policies" replace />} />
      {/* Also needs ?tab= — a bare /settings lands on Connectors (see the
          SETTINGS SUB-PAGES block above). 'trust-center' is a real
          SETTINGS_TABS id, so /trust-center was showing the Connectors page. */}
      <Route path="/trust-center" element={<Navigate to="/settings?tab=trust-center" replace />} />

      <Route path="*" element={<WithNavigate Component={NotFoundPage} />} />
    </Routes>
  );

  // Round 22 (2026-05-16): public share-link route. Renders WITHOUT sidebar
  // chrome, WITHOUT auth gating — token verification happens server-side via
  // the HMAC in the query string. Recipient could be a Slack/email link
  // forward to someone who has no CyberShield session at all. Path must
  // match the URL format emitted by POST /api/v1/issues/{id}/share.
  if (location.pathname.startsWith('/shared/')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/shared/:issueId" element={<PublicSharedIssue />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  // ── AUTH GATE ───────────────────────────────────────────────────────────
  // Block the app behind real login. Skip for embedded iframe mode, where the
  // parent window supplies the token via postMessage. While the initial
  // profile fetch is in flight we show a full-screen loader; once it resolves
  // with no session, the login page renders.
  if (!isEmbedded && !isAuthenticated) {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-screen bg-wiz-darker">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#325FE8]"></div>
        </div>
      );
    }
    return <LoginPage />;
  }

  // Show onboarding wizard on first login (full-screen, replaces the app)
  if (showOnboarding && isAuthenticated) {
    return (
      <OnboardingWizard onComplete={() => setShowOnboarding(false)} />
    );
  }

  if (isEmbedded) {
    return (
      <div className="h-screen bg-wiz-darker overflow-auto">
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            {routes}
          </Suspense>
        </ErrorBoundary>
        <KeyboardShortcutsModal open={shortcuts.open} onClose={shortcuts.close} />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[var(--bg-primary)] overflow-hidden">
      {/* Top progress bar for route transitions */}
      <TopProgressBar loading={loading} />
      {/* WCAG AA: Skip-to-content link for keyboard users */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      {/* Global real-time event notifier */}
      <SecurityEventNotifier />
      <Sidebar currentPath={location.pathname} onNavigate={(p: string) => {
        const closeAll = () => { setBoardsFlyoutOpen(false); setFindingsFlyoutOpen(false); setIssuesFlyoutOpen(false); setThreatsFlyoutOpen(false); };
        if (p === '/overview') { closeAll(); setBoardsFlyoutOpen(v => !v); return; }
        if (p === '/findings') { closeAll(); setFindingsFlyoutOpen(v => !v); return; }
        if (p === '/issues') { closeAll(); setIssuesFlyoutOpen(v => !v); return; }
        if (p === '/threats') { closeAll(); setThreatsFlyoutOpen(v => !v); return; }
        if (p === '/defend') { closeAll(); navigate(p); return; }
        closeAll(); navigate(p);
      }} />
      {/* ── Boards Flyout Panel (Wiz-style) ──────────────────────── */}
      {boardsFlyoutOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setBoardsFlyoutOpen(false)} />
          <div className="fixed left-[60px] top-0 bottom-0 w-[220px] bg-wiz-darker border-r border-wiz-border z-50 overflow-y-auto shadow-2xl"
            style={{ animation: 'slideIn 0.15s ease-out' }}>
            <div className="px-3 pt-4 pb-2">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-200">Boards</span>
                <button onClick={() => setBoardsFlyoutOpen(false)} className="text-gray-500 hover:text-gray-300 text-xs">→|</button>
              </div>
              {/* Default */}
              <button onClick={() => { navigate('/overview'); setBoardsFlyoutOpen(false); }}
                className={`w-full text-left px-2 py-1.5 rounded text-xs mb-0.5 flex items-center gap-2 ${location.pathname === '/overview' ? 'bg-purple-500/15 text-purple-400' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}>
                <span aria-hidden="true">{getBoardIcon('Overview', 'w-3.5 h-3.5')}</span>
                <span className="truncate">Overview</span>
              </button>
              <button onClick={() => { navigate('/defend'); setBoardsFlyoutOpen(false); }}
                className="w-full text-left px-2 py-1.5 rounded text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 mb-2 flex items-center gap-2">
                <span aria-hidden="true">{getBoardIcon('Threat Intel Center', 'w-3.5 h-3.5')}</span>
                <span className="truncate">Threat Intel Center</span>
              </button>
              {/* Managed */}
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1 mt-3">Managed by CyberShield</div>
              {[
                { label: 'Overview', path: '/overview' },
                { label: 'Cloud Cost', path: '/analytics' },
                { label: 'Secure Development', path: '/code-security' },
                { label: 'Security Score', path: '/overview' },
                { label: 'Attack Surface Management', path: '/easm' },
                { label: 'External Exposure', path: '/exposure' },
                { label: 'Identity Security', path: '/ciem' },
                { label: 'Secure Cloud Configuration', path: '/cspm' },
                { label: 'Secure Use of Secrets', path: '/cspm' },
                { label: 'Data Security', path: '/dspm' },
                { label: 'Container & Kubernetes Security', path: '/container-k8s' },
                { label: 'Serverless', path: '/cspm' },
                { label: 'Vulnerability Management', path: '/vulnerabilities' },
                { label: 'Patch Management', path: '/vulnerabilities' },
                { label: 'Non-Human Identities', path: '/ciem' },
                { label: 'AI Security', path: '/ai-spm' },
                { label: 'High Profile Threats', path: '/defend' },
                { label: 'Incident Readiness', path: '/remediation' },
              ].map(b => (
                <button key={b.label} onClick={() => { navigate(b.path); setBoardsFlyoutOpen(false); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-xs mb-0.5 flex items-center gap-2 ${
                    location.pathname === b.path ? 'text-gray-200 bg-gray-800' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                  }`}>
                  <span aria-hidden="true">{getBoardIcon(b.label, 'w-3.5 h-3.5')}</span>
                  <span className="truncate">{b.label}</span>
                </button>
              ))}

              {/* R144 — Custom Boards (loaded from /api/v1/dashboards/custom) */}
              <CustomBoardsFlyoutSection
                navigate={(p) => { navigate(p); setBoardsFlyoutOpen(false); }}
                currentPath={location.pathname}
              />
            </div>
          </div>
        </>
      )}
      {/* ── Findings Flyout Panel (Wiz-style) ────────────────────── */}
      {findingsFlyoutOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setFindingsFlyoutOpen(false)} />
          <div className="fixed left-[60px] top-0 bottom-0 w-[220px] bg-wiz-darker border-r border-wiz-border z-50 overflow-y-auto shadow-2xl"
            style={{ animation: 'slideIn 0.15s ease-out' }}>
            <div className="px-3 pt-4 pb-2">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-200">Findings</span>
                <button onClick={() => setFindingsFlyoutOpen(false)} className="text-gray-500 hover:text-gray-300 text-xs">→|</button>
              </div>

              <button onClick={() => { navigate('/findings'); setFindingsFlyoutOpen(false); }}
                className="w-full text-left px-2 py-2 rounded text-[13px] mb-2 flex items-center gap-2 text-white bg-gradient-to-r from-purple-600/30 to-blue-600/30 border border-purple-500/30 hover:border-purple-400/50 hover:from-purple-600/40 hover:to-blue-600/40 font-medium">
                <span className="text-sm">📋</span>
                <span>All Findings</span>
              </button>

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1">Security Posture</div>
              {[
                { label: 'Vulnerability Findings', icon: '🐛', path: '/vulnerabilities' },
                { label: 'Cloud Configuration Findings', icon: '☁️', path: '/issues?type=misconfiguration' },
                { label: 'Host Configuration Findings', icon: '🖥️', path: '/cwpp' },
                { label: 'Attack Surface Findings', icon: '🌐', path: '/exposure' },
                { label: 'Data Findings', icon: '🗄️', path: '/dspm' },
                { label: 'Secret Findings', icon: '🔑', path: '/issues?type=secrets_exposure' },
                { label: 'End of Life Findings', icon: '⏰', path: '/vulnerabilities?eol=true' },
                { label: 'SAST Findings', icon: '🔍', path: '/code-security' },
                { label: 'Network Exposure', icon: '🔗', path: '/issues?type=network_exposure' },
                { label: 'Excessive Access Findings', icon: '👤', path: '/ciem' },
              ].map(f => (
                <button key={f.label} onClick={() => { navigate(f.path); setFindingsFlyoutOpen(false); }}
                  className="w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50">
                  <span className="text-sm">{f.icon}</span>
                  <span className="truncate">{f.label}</span>
                </button>
              ))}

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1 mt-3">Threat Detection</div>
              <button onClick={() => { navigate('/defend'); setFindingsFlyoutOpen(false); }}
                className="w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50">
                <span className="text-sm">⚡</span><span>Detections</span>
              </button>

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1 mt-3">Secure Development</div>
              {[
                { label: 'Code & Build Scans', icon: '💻', path: '/code-security' },
                { label: 'Kubernetes Admission Reviews', icon: '☸️', path: '/container-k8s' },
              ].map(f => (
                <button key={f.label} onClick={() => { navigate(f.path); setFindingsFlyoutOpen(false); }}
                  className="w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50">
                  <span className="text-sm">{f.icon}</span><span className="truncate">{f.label}</span>
                </button>
              ))}

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1 mt-3">Cloud Ops</div>
              {[
                { label: 'Cost Optimization Opportunities', icon: '💰', path: '/analytics' },
                { label: 'Cost Monitor Findings', icon: '📊', path: '/analytics' },
              ].map(f => (
                <button key={f.label} onClick={() => { navigate(f.path); setFindingsFlyoutOpen(false); }}
                  className="w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50">
                  <span className="text-sm">{f.icon}</span><span className="truncate">{f.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      {/* ── Issues Flyout Panel (Wiz-style) ──────────────────────── */}
      {issuesFlyoutOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setIssuesFlyoutOpen(false)} />
          <div className="fixed left-[60px] top-0 bottom-0 w-[220px] bg-wiz-darker border-r border-wiz-border z-50 overflow-y-auto shadow-2xl"
            style={{ animation: 'slideIn 0.15s ease-out' }}>
            <div className="px-3 pt-4 pb-2">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-200">Issues</span>
                <button onClick={() => setIssuesFlyoutOpen(false)} className="text-gray-500 hover:text-gray-300 text-xs">→|</button>
              </div>

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1">Risk Issues</div>
              {[
                { label: 'All Risk Issues', icon: '🎯', path: '/issues', active: true },
                { label: 'Toxic Combinations', icon: '💀', path: '/issues?type=toxic_combination' },
                { label: 'Attack Path Issues', icon: '🔀', path: '/issues?type=attack_path' },
                { label: 'Cloud Configuration Issues', icon: '☁️', path: '/issues?type=misconfiguration' },
                { label: 'Vulnerability Issues', icon: '🐛', path: '/issues?type=vulnerability' },
                { label: 'Secret Exposure Issues', icon: '🔑', path: '/issues?type=secrets_exposure' },
                { label: 'Identity & Access Issues', icon: '👤', path: '/issues?type=identity_risk' },
                { label: 'Data Exposure Issues', icon: '🗄️', path: '/issues?type=data_exposure' },
                { label: 'Network Exposure Issues', icon: '🔗', path: '/issues?type=network_exposure' },
                { label: 'Runtime Threat Issues', icon: '⚡', path: '/issues?type=runtime_threat' },
              ].map(item => (
                <button key={item.label} onClick={() => { navigate(item.path); setIssuesFlyoutOpen(false); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 ${
                    item.active ? 'text-blue-400 bg-blue-500/10' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                  }`}>
                  <span className="text-sm">{item.icon}</span>
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      {/* ── Threats Flyout Panel (Wiz-style) ──────────────────────── */}
      {threatsFlyoutOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setThreatsFlyoutOpen(false)} />
          <div className="fixed left-[60px] top-0 bottom-0 w-[220px] bg-wiz-darker border-r border-wiz-border z-50 overflow-y-auto shadow-2xl"
            style={{ animation: 'slideIn 0.15s ease-out' }}>
            <div className="px-3 pt-4 pb-2">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-200">Threats</span>
                <button onClick={() => setThreatsFlyoutOpen(false)} className="text-gray-500 hover:text-gray-300 text-xs">→|</button>
              </div>

              <button onClick={() => { navigate('/threats'); setThreatsFlyoutOpen(false); }}
                className="w-full text-left px-2 py-1.5 rounded text-[12px] mb-2 flex items-center gap-2 text-blue-400 bg-blue-500/10">
                <span className="text-sm">🎯</span><span>All Threats</span>
              </button>

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1">Quick Filters</div>
              {[
                // Quick filters route to /issues — same filter contract Issues
                // page already supports (severity, status, created_after).
                // /defend doesn't parse query params, so the old links were
                // no-ops. Severity filter widely supported on Issues.
                { label: 'Critical/High open threats', icon: '🔴', path: '/issues?severity=critical,high' },
                { label: 'Triggered in the last week', icon: '⚡', path: '/issues?created_after=7d' },
                { label: 'Resolved in the last week', icon: '✅', path: '/issues?status=resolved&resolved_after=7d' },
              ].map(f => (
                <button key={f.label} onClick={() => { navigate(f.path); setThreatsFlyoutOpen(false); }}
                  className="w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50">
                  <span className="text-sm">{f.icon}</span><span className="truncate">{f.label}</span>
                </button>
              ))}

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1 mt-3">By Category</div>
              {[
                // By Category routes to /issues — these are exactly the
                // issue_type values the Issues classifier emits. /defend
                // doesn't render per-type, so the old paths were dead links.
                { label: 'Toxic Combinations', icon: '💀', path: '/issues?type=toxic_combination' },
                { label: 'Misconfiguration',   icon: '☁️',  path: '/issues?type=misconfiguration' },
                { label: 'Vulnerability',      icon: '🐛', path: '/issues?type=vulnerability' },
                { label: 'Identity Risk',      icon: '👤', path: '/issues?type=identity_risk' },
                { label: 'Network Exposure',   icon: '🌐', path: '/issues?type=network_exposure' },
                { label: 'Data Exposure',      icon: '🗄️', path: '/issues?type=data_exposure' },
                { label: 'Runtime Threats',    icon: '⚡', path: '/issues?type=runtime_threat' },
                { label: 'Secret Exposure',    icon: '🔑', path: '/issues?type=secrets_exposure' },
              ].map(f => (
                <button key={f.label} onClick={() => { navigate(f.path); setThreatsFlyoutOpen(false); }}
                  className="w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50">
                  <span className="text-sm">{f.icon}</span><span className="truncate">{f.label}</span>
                </button>
              ))}

              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 mb-1 mt-3">Threat Center</div>
              {[
                { label: 'Incidents', icon: '🚨', path: '/defend' },
                { label: 'Threat Actors', icon: '🎭', path: '/defend' },
                { label: 'Techniques (MITRE)', icon: '🗺️', path: '/defend' },
                { label: 'Tools', icon: '🔧', path: '/defend' },
                { label: 'Targeted Technologies', icon: '🎯', path: '/defend' },
                { label: 'Defenses (D3FEND)', icon: '🛡️', path: '/defend' },
              ].map(f => (
                <button key={f.label} onClick={() => { navigate(f.path); setThreatsFlyoutOpen(false); }}
                  className="w-full text-left px-2 py-1.5 rounded text-[12px] mb-0.5 flex items-center gap-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50">
                  <span className="text-sm">{f.icon}</span><span className="truncate">{f.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <style>{`@keyframes slideIn { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopNav currentPath={location.pathname} onNavigate={(p: string) => navigate(p)} />
        {/* Critical alert banner */}
        <AlertBanner onNavigate={(p: string) => navigate(p)} />
        {/* pb-20 reserves the strip the floating "Ask AI" button occupies —
            without it the FAB sat on top of whatever ended the page (Inventory's
            pagination row, the last table row), permanently covering a control. */}
        <main id="main-content" role="main" aria-label="Main content" className="flex-1 overflow-auto pb-20" tabIndex={-1}>
          <Breadcrumb />
          {/* Backend outage must never render as data — see ApiFailureBanner. */}
          <ApiFailureBanner />
          <ErrorBoundary resetKey={location.pathname} label="route">
            <Suspense fallback={<PageLoader />}>
              {routes}
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      {/* AI Assistant */}
      <AIAssistantWidget onNavigate={(p: string) => navigate(p)} />
      {/* Command Palette (Ctrl+K) */}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onNavigate={(p: string) => { navigate(p); setCommandPaletteOpen(false); }}
      />
      {/* Global keyboard shortcuts sheet (?) */}
      <KeyboardShortcutsModal open={shortcuts.open} onClose={shortcuts.close} />
    </div>
  );
};

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Default to the professional light theme; dark mode remains one click away
  // (top-bar toggle) and an explicit stored choice is always honoured, so
  // existing users who picked dark keep it. Only first-load users with no
  // stored preference now land on light.
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored === null) return false;
    return stored === 'true';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    document.documentElement.classList.toggle('light', !darkMode);
    localStorage.setItem('darkMode', String(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <ThemeProvider>
      <AuthProvider>
        <TenantProvider>
          <UserRoleProvider>
            <ToastProvider>
              <DialogProvider>
                <AIAdvisorProvider>
                  <FindingDrawerProvider>
                    <AppRoutes />
                  </FindingDrawerProvider>
                </AIAdvisorProvider>
              </DialogProvider>
            </ToastProvider>
          </UserRoleProvider>
        </TenantProvider>
      </AuthProvider>
    </ThemeProvider>
  </BrowserRouter>
);

export default App;
